import axios, { AxiosResponse } from "axios";
import { AuthenticationResponse } from "../interfaces/authentication/AuthenticationResponse";
import { UserResponse } from "../interfaces/authentication/UserResponse";
import { AN_ERROR_OCCURRED_MESSAGE } from "../utils/constants";
import { getAuthenticationHeaders } from "../utils/authenticationsUtils";

const AUTHENTICATION_API_URL: string = `${process.env.REACT_APP_API_URL}/authentication`;

const handleRequestError = (error: any): string => {
  return error.response?.data?.message || AN_ERROR_OCCURRED_MESSAGE;
};

const processAuthenticationResponse = (response: any, storeUser: boolean = false): AuthenticationResponse => {
  const authenticationData = response.data;

  if (storeUser && authenticationData.accessToken) {
    localStorage.setItem("user", JSON.stringify(authenticationData));
  }

  return authenticationData;
};

const register = async (
  userName: string,
  email: string,
  password: string,
  confirmPassword: string
): Promise<void> => {
  try {
    await axios.post<void>(`${AUTHENTICATION_API_URL}/register`, {
      userName,
      email,
      password,
      confirmPassword,
    });
  } catch (error) {
    throw new Error(handleRequestError(error));
  }
};

const login = async (
  userName: string,
  password: string
): Promise<AuthenticationResponse> => {
  try {
    const loginResponse: AxiosResponse<AuthenticationResponse, any> =
      await axios.post<AuthenticationResponse>(
        `${AUTHENTICATION_API_URL}/login`,
        { userName, password }
      );
    return processAuthenticationResponse(loginResponse, true);
  } catch (error) {
    throw new Error(handleRequestError(error));
  }
};

const logout = (): void => {
  localStorage.removeItem("user");
};

const refreshToken = async (): Promise<AuthenticationResponse> => {
  const user = JSON.parse(localStorage.getItem("user") || "null") as AuthenticationResponse | null;

  if (user?.refreshToken) {
    try {
      const refreshTokenResponse: AxiosResponse<AuthenticationResponse, any> =
        await axios.post<AuthenticationResponse>(
          `${AUTHENTICATION_API_URL}/refresh-token`,
          {
            accessToken: user.accessToken,
            refreshToken: user.refreshToken,
          }
        );

      return processAuthenticationResponse(refreshTokenResponse, true);
    } catch (error) {
      throw new Error(handleRequestError(error));
    }
  }

  return Promise.reject(new Error("No refresh token available"));
};

export const getAllUsers = async (): Promise<UserResponse[]> => {
  const headers = getAuthenticationHeaders();
  const getAllUsersResponse = await axios.get(`${AUTHENTICATION_API_URL}/get-all-users`, { headers });
  return getAllUsersResponse.data;
};

const authenticationService = {
  register,
  login,
  logout,
  refreshToken,
  getAllUsers,
};

export default authenticationService;